import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import Button from '../Components/Button';

// const LevelCard = ({ color, title, description }) => {
//   return (
//     <div className="flex items-center gap-4">
//       <div className="bg-white p-3 rounded-full">
//         <div style={{ backgroundColor: color }} className="w-4 h-4 rounded-full" />
//       </div>
//       <div>
//         <h3 className="text-[18px] font-bold text-[#fff] mb-1">{title}</h3>
//         <p className="text-[14px] text-[#fff] max-w-[300px]">{description}</p>
//       </div>
//     </div>
//   );
// };

const LevelsSection = () => {
  const { isDarkMode } = useTheme();

  const levels = [
    {
      color: "#FF6B6B",
      title: "Beginner",
      description: "Step into the world of AI with easy-to-follow quests and guidance designed for newcomers"
    },
    {
      color: "#FFD93D",
      title: "Intermediate",
      description: "Challenge yourself with real-world problems and build confidence in your abilities"
    },
    {
      color: "#4CAF50",
      title: "Advanced",
      description: "Tackle complex quests, innovate with cutting-edge AI, and make a real impact"
    }
  ];

  return (
    <section className={`relative py-16 px-4 font-lexend ${isDarkMode ? 'bg-[#000000]' : 'bg-white'}`}>
      <div className="max-w-[1300px] mx-auto">
        {/* Desktop and Tablet layout */}
        <div className="hidden sm:grid sm:grid-cols-[300px,auto,1fr] md:grid-cols-[400px,auto,1fr] lg:grid-cols-[500px,auto,1fr] gap-6 md:gap-8 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="text-left">
            <motion.h2 
              className={`text-[24px] md:text-[32px] lg:text-[36px] font-bold font-lexend ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} mb-4`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Start at Your Level,<br />Grow With Lunor
            </motion.h2>
            
            <motion.p 
              className={`text-[14px] md:text-[16px] lg:text-[18px] font-lexend font-light ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} mb-8 max-w-[500px]`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Whether you're just starting out or are an experienced professional, LunorQuest adapts to your expertise, helping you elevate your skills with every quest.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Button 
                text="Host Your Quest"
                className="gradient"
                link="https://app.lunor.quest/"
              />
            </motion.div>
          </div>

          {/* Center and Right Content */}
          <div className="flex gap-6 md:gap-8 lg:gap-16 flex-1">
            <motion.div
              className="flex-shrink-0"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 1,
                ease: "easeOut"
              }}
            >
              <motion.div 
                className={`p-4 sm:p-5 md:p-6 rounded-full flex flex-col justify-between ${
                  isDarkMode 
                    ? 'bg-[#161513] border-[#554F3C]' 
                    : 'bg-[#E8E8E8] border-[#CDCDCD]'
                } border`}
                style={{ 
                  height: '350px',
                  '@media (minWidth: 768px)': {
                    height: '400px'
                  },
                  '@media (minWidth: 1024px)': {
                    height: '450px'
                  }
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: 0.3,
                  ease: "easeOut"
                }}
              >
                {levels.map((level, index) => (
                  <motion.div
                    key={level.title}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: 1 + (index * 0.3),
                      duration: 0.5,
                      ease: "easeOut"
                    }}
                  >
                    <div
                      style={{ backgroundColor: level.color }}
                      className="w-[55px] h-[55px] sm:w-[65px] sm:h-[65px] md:w-[71px] md:h-[71px] lg:w-[80px] lg:h-[80px] rounded-full"
                    />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Level Descriptions */}
            <motion.div
              className="flex flex-col justify-between flex-1"
              style={{ 
                height: '350px',
                '@media (minWidth: 768px)': {
                  height: '400px'
                },
                '@media (minWidth: 1024px)': {
                  height: '450px'
                }
              }}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              {levels.map((level, index) => (
                <motion.div
                  key={level.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + (index * 0.1) }}
                  className="text-left"
                >
                  <div className="w-full">
                    <h3 className={`text-[18px] md:text-[22px] lg:text-[26px] font-bold font-lexend ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} mb-2 `}>{level.title}</h3>
                    <p className={`text-[14px] md:text-[16px] lg:text-[18px] font-lexend font-light ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} w-full mb-8`}>{level.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Mobile Layout - Only shows below sm breakpoint */}
        <div className="flex flex-col gap-12 sm:hidden">
          {/* Mobile content - Left aligned */}
          <div className="text-left max-w-[500px]">
            <motion.h2 
              className={`text-[24px] font-bold text-center md:text-left font-lexend ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} mb-4`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Start at Your Level, Grow With Lunor
            </motion.h2>
            
            <motion.p 
              className={`text-[14px] font-lexend font-light text-center md:text-left ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} mb-8 max-w-[500px]`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Whether you're just starting out or are an experienced professional, LunorQuest adapts to your expertise, helping you elevate your skills with every quest.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex justify-center md:justify-start"
            >
              <Button 
                text="Host Your Quest"
                className="gradient"
                link="https://app.lunor.quest/"
              />
            </motion.div>
          </div>

          {/* Mobile Levels Content */}
          <div className="flex gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 1,
                ease: "easeOut"
              }}
            >
              <motion.div 
                className={`p-6 rounded-full flex flex-col justify-between ${
                  isDarkMode 
                    ? 'bg-[#161513] border-[#554F3C]' 
                    : 'bg-[#E8E8E8] border-[#CDCDCD]'
                } border`} 
                style={{ height: '450px' }}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: 0.3,
                  ease: "easeOut"
                }}
              >
                {levels.map((level, index) => (
                  <motion.div
                    key={level.title}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ 
                      delay: 1 + (index * 0.3),
                      duration: 0.5,
                      ease: "easeOut"
                    }}
                  >
                    <div
                      style={{ backgroundColor: level.color }}
                      className="w-[85px] h-[85px] rounded-full"
                    />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Level Descriptions */}
            <div className="flex flex-col justify-between flex-1" style={{ height: '450px' }}>
              {levels.map((level, index) => (
                <motion.div
                  key={level.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + (index * 0.1) }}
                  className="text-left"
                >
                  <div className="w-full">
                    <h3 className={`text-[20px] sm:text-[24px] md:text-[26px] font-bold font-lexend ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} mb-2`}>{level.title}</h3>
                    <p className={`text-[16px] sm:text-[18px] font-lexend font-light ${isDarkMode ? 'text-[#fff]' : 'text-[#000]'} w-full`}>{level.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LevelsSection; 